import { useState, useEffect, useCallback, useRef } from 'react';
import supabase from '../supabaseClient/supabaseClient';

export function useAiAllowanceState(userId) {
  const [usage, setUsage] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const current = useRef(userId);
  const sequence = useRef(0);
  current.current = userId;
  const invalidate = useCallback(() => { sequence.current += 1; }, []);
  const refresh = useCallback(async () => {
    if (!userId) return;
    const request = ++sequence.current;
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;
      const response = await fetch(`${process.env.REACT_APP_BE_URL}/ai-usage`, { headers: { Authorization: `Bearer ${session.access_token}` } });
      if (!response.ok) throw new Error('Daily usage is temporarily unavailable.');
      const value = await response.json();
      if (current.current === userId && sequence.current === request) { setUsage(value); setError(''); }
    } catch {
      if (current.current === userId && sequence.current === request) setError('Daily usage is temporarily unavailable.');
    } finally { if (current.current === userId && sequence.current === request) setLoading(false); }
  }, [userId]);
  useEffect(() => {
    setUsage(null); setError('');
    if (!userId) return;
    refresh();
    const receive = event => {
      if (event.detail.userId !== userId) return;
      invalidate(); setUsage(event.detail.usage); setError(''); setLoading(false);
    };
    const visible = () => { if (document.visibilityState === 'visible') refresh(); };
    window.addEventListener('worksheep-ai-usage', receive);
    window.addEventListener('worksheep-ai-refresh', refresh);
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', visible);
    return () => {
      invalidate();
      window.removeEventListener('worksheep-ai-usage', receive);
      window.removeEventListener('worksheep-ai-refresh', refresh);
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', visible);
    };
  }, [userId, refresh, invalidate]);
  useEffect(() => {
    if (!usage?.resetsAt) return;
    const untilReset = Math.max(1000, Date.parse(usage.resetsAt) - Date.now() + 250);
    const delay = usage.pending > 0 ? Math.min(untilReset, 5000) : untilReset;
    const timer = setTimeout(refresh, Math.min(delay, 2147483647));
    return () => clearTimeout(timer);
  }, [usage, refresh]);
  return { usage, error, loading, refresh };
}
